import { Button } from "@ui/Button";
import { Stack } from "@ui/Stack";
import { toast } from "@ui/molecules/Toasts/toast";
import { getErrorMessage } from "@utils/getErrorMessage";
import type { AgencyHubGroupListItem } from "@/client";
import { restoreAgencyHubGroup } from "@/client";

export const REVERT_ACTION_RESTORE_AGENCY_HUB_GROUP =
	"restore_agency_hub_group" as const;

export type RestoreAgencyHubGroupRevertAction = {
	type: typeof REVERT_ACTION_RESTORE_AGENCY_HUB_GROUP;
	group_public_id: string;
	agency_public_id: string;
};

export const canUndoGroupNotification = (
	revertAction: Record<string, unknown> | null | undefined,
): revertAction is RestoreAgencyHubGroupRevertAction =>
	revertAction?.type === REVERT_ACTION_RESTORE_AGENCY_HUB_GROUP &&
	typeof revertAction.group_public_id === "string" &&
	typeof revertAction.agency_public_id === "string";

export const groupDeleteNotificationDescription = (notification: {
	description?: string | null;
}): string => notification.description ?? "";

const groupRemovedMessage = (name: string | null | undefined) =>
	name ? `"${name}" was removed.` : "Group was removed.";

export async function restoreGroupFromRevertAction(
	revertAction: Record<string, unknown> | null | undefined,
	options?: { onRestored?: () => void },
): Promise<boolean> {
	if (!canUndoGroupNotification(revertAction)) {
		return false;
	}

	const restoreRes = await restoreAgencyHubGroup({
		path: { group: revertAction.group_public_id },
		headers: {
			Accept: "application/json",
			"As-Agency": revertAction.agency_public_id,
		},
	});

	if (restoreRes.error) {
		toast.error({
			title: "Could not restore group",
			description: getErrorMessage(restoreRes.error),
		});
		return false;
	}

	toast.success({ title: "Group restored" });
	options?.onRestored?.();
	return true;
}

const restoreToastDescription =
	(options: {
		onRestore: () => Promise<void>;
		message: string;
	}) =>
	({ handleClose }: { handleClose: () => void }) => (
		<Stack direction="column" spacing="small">
			<span>{options.message}</span>
			<Button
				variant="secondary"
				theme="monochrome"
				onClick={async () => {
					await options.onRestore();
					handleClose();
				}}
			>
				Restore
			</Button>
		</Stack>
	);

type ShowUndoableGroupDeleteToastOptions = {
	group: AgencyHubGroupListItem;
	agencyPublicId: string;
	onRestored: () => void;
};

export const showUndoableGroupDeleteToast = (
	options: ShowUndoableGroupDeleteToastOptions,
) => {
	const message = groupRemovedMessage(options.group.name);
	const revertAction: RestoreAgencyHubGroupRevertAction = {
		type: REVERT_ACTION_RESTORE_AGENCY_HUB_GROUP,
		group_public_id: options.group.public_id,
		agency_public_id: options.agencyPublicId,
	};

	const onRestore = async () => {
		await restoreGroupFromRevertAction(revertAction, {
			onRestored: options.onRestored,
		});
	};

	toast.success({
		duration: 30000,
		store: true,
		storeType: "info",
		storeDescription: message,
		revert_action: revertAction,
		title: "Group deleted",
		description: restoreToastDescription({ onRestore, message }),
	});
};
